import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DigitalConsent from './digital-concent/DigitalConsent';
import PrivacyNotification from './privacy-notification/PrivacyNotification';
import Questionnaire from './questionnaire/Questionnaire';
import ThankYouScreen from './thank-you-screen/ThankYouScreen';
import WelcomeScreen from './welcome-screen/WelcomeScreen';
import { Question } from 'src/types/questionnaire.types';
import { useAppContext } from 'src/app/state';
import { ActionType } from 'src/app/state/action-type.enum';
import { AppRoutes } from 'src/types/app-routes.enum';
import { getDateRange } from 'src/app/utils/calendar.utils';

const EnrollmentPage = () => {
  const {
    state: { questionnaire },
    dispatch,
  } = useAppContext();
  const navigate = useNavigate();
  const [currentScreen, setCurrentScreen] = useState(1);
  const [questionnaireResults, setQuestionnaireResults] = useState<Question[]>(
    []
  );
  const createDoctorAppointment = (date: Date, doctorType: string) => {
    dispatch({
      type: ActionType.SET_DOCTOR_APPOINTMENT,
      payload: {
        doctorAppointment: {
          doctorType,
          appointmentDate: date,
        },
      },
    });
  };
  const createVyduraPreventionDates = (
    startDate: Date,
    prescribtionDate: Date
  ) => {
    const vyduraPreventiveDays = getDateRange(
      new Date(startDate),
      new Date(new Date().getFullYear(), 11, 31)
    );
    dispatch({
      type: ActionType.SET_MIGRAINE_ATTACK,
      payload: {
        migraineAttacks: vyduraPreventiveDays.map((date) => ({
          date,
          vyduraPerAttack: false,
          vyduraEveryOtherDay: true,
          isMigraineDay: false,
        })),
      },
    });

    dispatch({
      type: ActionType.SET_VYDURA_SCHEDULE,
      payload: {
        vyduraSchedule: {
          startDate,
          prescribtionDate,
          frequency: 'Every other day',
          isActive: true,
        },
      },
    });
  };
  const createEmptyVyduraNotification = (pills: number) => {
    dispatch({
      type: ActionType.SET_VYDURA_REMINDER,
      payload: {
        vyduraReminder: {
          pills,
        },
      },
    });
  };
  const onContinueEnrollment = useCallback(
    () => setCurrentScreen((prev) => prev + 1),
    []
  );
  const onQuestionnaireComplete = (result: Question[]) => {
    setQuestionnaireResults(result);
    onContinueEnrollment();
  };
  const onFinish = useCallback(() => navigate(AppRoutes.Home), []);
  const onEnrollmentComplete = () => {
    const doctorAppointment = questionnaireResults.find(
      (question) => question.isDoctorAppointmentCreation
    );
    const isAppointmentScheduled =
      doctorAppointment?.choices &&
      doctorAppointment.choices[0]?.title.toLowerCase() === 'yes';
    if (
      isAppointmentScheduled &&
      doctorAppointment.date &&
      doctorAppointment.selections?.length
    ) {
      createDoctorAppointment(
        doctorAppointment.date,
        doctorAppointment.selections[0].title
      );
    }
    const vyduraPrescribtion = questionnaireResults.find(
      (question) => question.title === 'How many Vydura doses do you have left?'
    )?.choices;

    if (vyduraPrescribtion?.length === 1) {
      createEmptyVyduraNotification(Number(vyduraPrescribtion[0].title));
    }

    const vyduraIntakeStartDate = questionnaireResults.find(
      (question) => question.title === 'What day did you first take Vydura?'
    )?.date;
    const vyduraPrescribtionDate = questionnaireResults.find(
      (question) => question.title === 'When were you first prescribed Vydura?'
    )?.date;

    if (vyduraIntakeStartDate && vyduraPrescribtionDate) {
      createVyduraPreventionDates(
        vyduraIntakeStartDate,
        vyduraPrescribtionDate
      );
    }

    onFinish();
  };

  return (
    <>
      <DigitalConsent
        isVisible={currentScreen === 1}
        onContinue={onContinueEnrollment}
        onClose={onFinish}
      />
      <PrivacyNotification
        isVisible={currentScreen === 2}
        onContinue={onContinueEnrollment}
        onClose={onFinish}
      />
      <WelcomeScreen
        isVisible={currentScreen === 3}
        onContinue={onContinueEnrollment}
        onClose={onFinish}
      />
      <Questionnaire
        show={currentScreen === 4}
        onClose={onFinish}
        onSaveSelections={onQuestionnaireComplete}
        questions={questionnaire}
      />
      <ThankYouScreen
        isVisible={currentScreen === 5}
        onClose={onEnrollmentComplete}
      />
    </>
  );
};

export default EnrollmentPage;
